import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    TrendingDown,
    Bug,
    Shield,
    Database,
} from 'lucide-react';
import { fetchQASummary } from '../data/adapters';
import { QASummary } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const QADashboardTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [data, setData] = useState<QASummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const qaData = await fetchQASummary(isDemo).catch(() => null);
                setData(qaData);
                if (!qaData) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'blocker':
                return 'text-red-400 bg-red-500/20';
            case 'critical':
                return 'text-orange-400 bg-orange-500/20';
            case 'major':
                return 'text-yellow-400 bg-yellow-500/20';
            case 'minor':
                return 'text-blue-400 bg-blue-500/20';
            default:
                return 'text-slate-400 bg-slate-500/20';
        }
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'blocker':
                return <XCircle size={16} className="text-red-400" />;
            case 'critical':
                return <AlertTriangle size={16} className="text-orange-400" />;
            case 'major':
                return <AlertTriangle size={16} className="text-yellow-400" />;
            case 'minor':
                return <Bug size={16} className="text-blue-400" />;
            default:
                return <Bug size={16} className="text-slate-400" />;
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
                {/* QA Metrics Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData || !data) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Test Cases</h3>
                            <CheckCircle size={20} className="text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Pass Rate</h3>
                            <CheckCircle size={20} className="text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Total Defects</h3>
                            <Bug size={20} className="text-red-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Median TTF</h3>
                            <Clock size={20} className="text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--h</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Defects by Severity</h3>
                    {renderNoDataPlaceholder('Defect severity data')}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">Defect Leakage</h3>
                        {renderNoDataPlaceholder('Leakage data')}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            Rework & Efficiency
                        </h3>
                        {renderNoDataPlaceholder('Efficiency metrics')}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                    {renderNoDataPlaceholder('Recommendations')}
                </motion.div>
            </div>
        );
    }

    const totalDefects = data.defectsBySeverity.reduce((sum, d) => sum + d.count, 0);
    const totalLeakage = data.leakageToUAT + data.leakageToProd;

    return (
        <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Test Cases */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Test Cases</h3>
                        <CheckCircle size={20} className="text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {data.totalCases.toLocaleString()}
                    </div>
                    <p className="text-sm text-slate-400">Total test cases executed</p>
                </motion.div>

                {/* Pass Rate */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Pass Rate</h3>
                        <CheckCircle size={20} className="text-green-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.passPct}%</div>
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                        <div
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${data.passPct}%` }}
                        />
                    </div>
                    <p className="text-sm text-slate-400">Tests passing successfully</p>
                </motion.div>

                {/* Defect Count */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Total Defects</h3>
                        <Bug size={20} className="text-red-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{totalDefects}</div>
                    <p className="text-sm text-slate-400">Defects found during testing</p>
                </motion.div>

                {/* Time to Fix */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Median TTF</h3>
                        <Clock size={20} className="text-purple-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {data.medianTimeToFixHours}h
                    </div>
                    <p className="text-sm text-slate-400">Median time to fix defects</p>
                </motion.div>
            </div>

            {/* Defects by Severity */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Defects by Severity</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data.defectsBySeverity.map((defect, index) => (
                        <motion.div
                            key={defect.severity}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center"
                        >
                            <div className="flex items-center justify-center mb-3">
                                {getSeverityIcon(defect.severity)}
                            </div>
                            <div className="text-2xl font-bold text-white mb-2">{defect.count}</div>
                            <div
                                className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getSeverityColor(defect.severity)}`}
                            >
                                {defect.severity}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Leakage & Rework */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Defect Leakage */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-white">Defect Leakage</h3>
                        <div className="flex items-center space-x-2 text-sm text-slate-400">
                            <Shield size={16} />
                            <span>Defects reaching UAT/Production</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                                <span className="text-slate-300">UAT Environment</span>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-yellow-400">
                                    {data.leakageToUAT}
                                </div>
                                <div className="text-sm text-slate-400">defects</div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                                <span className="text-slate-300">Production</span>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-red-400">
                                    {data.leakageToProd}
                                </div>
                                <div className="text-sm text-slate-400">defects</div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-600">
                            <span className="text-slate-300 font-medium">Total Leakage</span>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-white">{totalLeakage}</div>
                                <div className="text-sm text-slate-400">defects</div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Rework & Efficiency */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Rework & Efficiency</h3>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <TrendingDown size={16} className="text-orange-400" />
                                <span className="text-slate-300">Rework Rate</span>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-orange-400">
                                    {data.reworkRatePct}%
                                </div>
                                <div className="text-sm text-slate-400">of test cases</div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <Clock size={16} className="text-blue-400" />
                                <span className="text-slate-300">Median Time to Fix</span>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-blue-400">
                                    {data.medianTimeToFixHours}h
                                </div>
                                <div className="text-sm text-slate-400">hours</div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <CheckCircle size={16} className="text-green-400" />
                                <span className="text-slate-300">Test Efficiency</span>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-green-400">
                                    {Math.round(
                                        ((data.totalCases - totalDefects) / data.totalCases) * 100
                                    )}
                                    %
                                </div>
                                <div className="text-sm text-slate-400">efficiency</div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Recommendations */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.passPct < 90 && (
                        <div className="flex items-start space-x-3 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                            <CheckCircle size={16} className="text-yellow-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-yellow-300">
                                    Improve Test Pass Rate
                                </h4>
                                <p className="text-sm text-yellow-200">
                                    Current pass rate is below 90%. Review failing tests and update
                                    test cases.
                                </p>
                            </div>
                        </div>
                    )}

                    {totalLeakage > 5 && (
                        <div className="flex items-start space-x-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                            <Shield size={16} className="text-red-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-red-300">Reduce Defect Leakage</h4>
                                <p className="text-sm text-red-200">
                                    {totalLeakage} defects leaked to UAT/Production. Strengthen
                                    testing gates.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.reworkRatePct > 10 && (
                        <div className="flex items-start space-x-3 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                            <TrendingDown size={16} className="text-orange-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-orange-300">High Rework Rate</h4>
                                <p className="text-sm text-orange-200">
                                    {data.reworkRatePct}% rework rate indicates process
                                    inefficiencies.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.medianTimeToFixHours > 8 && (
                        <div className="flex items-start space-x-3 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                            <Clock size={16} className="text-blue-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-blue-300">Optimize Fix Time</h4>
                                <p className="text-sm text-blue-200">
                                    Median fix time of {data.medianTimeToFixHours}h suggests process
                                    improvements needed.
                                </p>
                            </div>
                        </div>
                    )}

                    {data.passPct >= 90 && totalLeakage <= 5 && data.reworkRatePct <= 10 && (
                        <div className="flex items-start space-x-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg md:col-span-2">
                            <CheckCircle size={16} className="text-green-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-green-300">
                                    Excellent QA Performance
                                </h4>
                                <p className="text-sm text-green-200">
                                    All QA metrics are within acceptable ranges. Continue monitoring
                                    and maintain quality standards.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
